import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  Smartphone,
  Loader2,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Package,
} from 'lucide-react';
import { authService, otpService as httpPhoneLogin } from '../api/auth';
import { otpService } from '../api/otp';
import { firebaseOTP } from '../api/firebaseOTP';
import { allowDevOtpAssistUI } from '../api/otpEnv';
import { API_URL } from '../api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { resumePendingAuthActionAfterLogin } from '../utils/runPendingAuthAction';
import { AUTH_REQUIRED_MESSAGE } from '../utils/customerAuth';
import { getImageUrl } from '../utils/imageUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const LOGIN_BENEFITS = [
  {
    icon: Package,
    title: 'Your orders',
    desc: 'View history, track shipments, and download invoices.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure sign-in',
    desc: 'Protected account with email or mobile OTP.',
  },
  {
    icon: Smartphone,
    title: 'Quick OTP login',
    desc: 'Sign in with your registered mobile number.',
  },
] as const;

const inputBase =
  'w-full text-sm text-slate-900 bg-white border border-slate-200 rounded-lg shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors disabled:bg-slate-50 disabled:text-slate-500';

type LoginMode = 'email' | 'mobile';

const Login = () => {
  const [mode, setMode] = useState<LoginMode>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [devOtpCode, setDevOtpCode] = useState<string | null>(null);
  const [exposeDevCodeFromApi, setExposeDevCodeFromApi] = useState(false);
  const [backupSmsConfigured, setBackupSmsConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [storeBrand, setStoreBrand] = useState({ name: 'NLS Store', logo: '' });
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const { addToCart: addToCartLocal } = useCart();

  const locationState = location.state as { from?: string | { pathname?: string }; authMessage?: string } | null;
  const destinationAfterLogin =
    (typeof locationState?.from === 'string'
      ? locationState.from
      : locationState?.from?.pathname) || '/profile';
  const authBannerMessage = locationState?.authMessage || AUTH_REQUIRED_MESSAGE;

  useEffect(() => {
    fetch(`${API_URL}/admin/ultra-settings/public/store`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.success && data.data) {
          setLogoLoadFailed(false);
          setStoreBrand({
            name: data.data.storeName || 'NLS Store',
            logo: getImageUrl(data.data.storeLogo || ''),
          });
        }
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    setError('');
    setSuccess('');
    setOtpSent(false);
    setOtp('');
    setDevOtpCode(null);
  }, [mode]);

  useEffect(() => {
    return () => {
      void firebaseOTP.teardownRecaptcha('recaptcha-container');
    };
  }, []);

  useEffect(() => {
    if (mode !== 'mobile') return;
    fetch(`${API_BASE_URL}/otp/provider-config`)
      .then((r) => r.json())
      .then((pc) => {
        setBackupSmsConfigured(!!pc?.firebaseOtp?.backupSmsAvailable);
        setExposeDevCodeFromApi(pc?.otpRuntime?.exposeDevCode === true);
      })
      .catch(() => {
        setBackupSmsConfigured(false);
        setExposeDevCodeFromApi(false);
      });
  }, [mode]);

  const goAfterSuccess = async () => {
    setSuccess('Signed in! Continuing…');
    checkAuth();
    const redirectPath = await resumePendingAuthActionAfterLogin({
      addToCartLocal,
      navigate: (path, options) => navigate(path, { ...(options || {}), replace: true }),
    });
    const target = redirectPath || destinationAfterLogin;
    setTimeout(() => navigate(target, { replace: true }), 400);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const trimmedEmail = email.trim();
      if (!trimmedEmail || !password) {
        setError('Please enter your email and password.');
        setLoading(false);
        return;
      }

      await authService.loginCustomerOnly({ email: trimmedEmail, password });
      goAfterSuccess();
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Sign-in failed. Check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setError('');
    setSuccess('');
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    setLoading(true);
    setDevOtpCode(null);
    try {
      const result = await otpService.sendOTP(digits);
      if (result.success) {
        setOtpSent(true);
        const devAssist = allowDevOtpAssistUI(exposeDevCodeFromApi);
        const devOtp = devAssist ? (result as { devOtp?: string }).devOtp : undefined;
        if (devOtp) {
          setDevOtpCode(devOtp);
          setOtp(devOtp);
          setSuccess(
            'Dev-only: OTP shown below because the API allowed it — production never exposes codes.'
          );
        } else {
          setDevOtpCode(null);
          setSuccess(
            (result as { message?: string }).message ||
              'OTP sent. Enter the code from your phone.'
          );
        }
      } else {
        setError(result.message || 'Could not send OTP.');
      }
    } catch (err: any) {
      setError(err.message || 'Could not send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleMobileLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    setSuccess('');

    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10 || otp.replace(/\D/g, '').length !== 6) {
      setError('Enter your 10-digit number and the 6-digit OTP.');
      setLoading(false);
      return;
    }

    try {
      const otpDigits = otp.replace(/\D/g, '');
      const pc = await fetch(`${API_BASE_URL}/otp/provider-config`).then((r) =>
        r.json()
      );
      let firebaseIdToken: string | undefined;
      if (pc.provider === 'firebase') {
        const vr = await otpService.verifyOTP(digits, otpDigits);
        firebaseIdToken = vr.firebaseIdToken;
        if (process.env.NODE_ENV === 'production' && !firebaseIdToken) {
          setError(
            'Phone verification did not complete. Request a new OTP and try again, or refresh the page.'
          );
          setLoading(false);
          return;
        }
      }
      const result = await httpPhoneLogin.phoneLoginCustomerOnly(
        digits,
        otpDigits,
        firebaseIdToken
      );
      if (result.success && result.token && result.user) {
        goAfterSuccess();
      } else if (result.success && !result.userExists) {
        setError('No account found for this number. Please create an account first.');
      } else if (result.success && result.userExists && !result.token) {
        setError('Sign-in incomplete — no session returned. Try again or contact support.');
      } else {
        setError(result.message || 'Sign-in failed.');
      }
    } catch (err: any) {
      setError(err.message || 'Sign-in failed.');
    } finally {
      setLoading(false);
    }
  };

  const showSidebarLogo = Boolean(storeBrand.logo) && !logoLoadFailed;

  return (
    <div className="min-h-screen bg-slate-50 pt-[72px] sm:pt-20">
      <div
        id="recaptcha-container"
        className="firebase-recaptcha-host pointer-events-none fixed bottom-0 left-0 right-0 z-[9999] min-h-0"
        aria-live="polite"
      />

      <aside className="fixed left-0 top-[72px] bottom-0 z-0 hidden w-[380px] flex-col bg-slate-900 text-white border-r border-slate-800 sm:top-20 lg:flex xl:w-[400px]">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950" />
        <div className="pointer-events-none absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_70%_40%_at_100%_0%,rgba(220,38,38,0.22),transparent_55%)]" />

        <div className="relative z-10 flex h-full min-h-0 flex-col">
          <div className="shrink-0 px-8 pb-6 pt-8 xl:px-10 xl:pt-9">
            <Link to="/" className="inline-flex items-center">
              {showSidebarLogo ? (
                <img
                  src={storeBrand.logo}
                  alt={storeBrand.name}
                  className="h-10 w-auto max-w-[200px] object-contain"
                  onError={() => setLogoLoadFailed(true)}
                />
              ) : (
                <span className="text-xl font-bold tracking-tight text-white">{storeBrand.name}</span>
              )}
            </Link>
          </div>

          <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto px-8 pb-8 xl:px-10">
            <h1 className="text-[1.35rem] font-bold leading-snug tracking-tight text-white xl:text-2xl">
              Welcome back
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Sign in to {storeBrand.name} to shop, track orders, and manage your account.
            </p>

            <ul className="mt-8 space-y-6">
              {LOGIN_BENEFITS.map(({ icon: Icon, title, desc }) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/12 bg-white/5 text-primary-300">
                    <Icon className="h-[0.95rem] w-[0.95rem]" strokeWidth={2} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-100">{title}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-slate-500">{desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className="shrink-0 border-t border-white/10 px-8 py-4 text-[11px] text-slate-600 xl:px-10">
            © {new Date().getFullYear()} {storeBrand.name}
          </p>
        </div>
      </aside>

      <div className="relative z-[1] flex flex-col lg:min-h-[calc(100vh-5rem)] lg:pl-[380px] xl:pl-[400px]">
        <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8 lg:px-12 lg:py-12">
          <div className="w-full max-w-[480px]">
            <div className="mb-8 lg:hidden text-center">
              {showSidebarLogo ? (
                <img
                  src={storeBrand.logo}
                  alt={storeBrand.name}
                  className="mx-auto mb-4 h-9 object-contain"
                />
              ) : (
                <p className="mb-2 text-lg font-bold text-slate-900">{storeBrand.name}</p>
              )}
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Sign in</h2>
              <p className="mt-2 text-sm text-slate-600 sm:text-base">
                New to {storeBrand.name}?{' '}
                <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700">
                  Create an account
                </Link>
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-200/50 sm:p-8">
              <div className="mb-6 flex items-center gap-2 border-b border-slate-100 pb-6 text-sm text-slate-600">
                <ShieldCheck className="h-4 w-4 shrink-0 text-primary-600" />
                <span>Secure customer sign-in — your details stay private.</span>
              </div>

              <div className="mb-6 flex rounded-lg bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => setMode('email')}
                  className={`flex-1 rounded-md py-2.5 text-sm font-semibold transition-colors ${
                    mode === 'email'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setMode('mobile')}
                  className={`flex-1 rounded-md py-2.5 text-sm font-semibold transition-colors ${
                    mode === 'mobile'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Mobile OTP
                </button>
              </div>

              {locationState?.authMessage && (
                <div className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  {authBannerMessage}
                </div>
              )}

              {error && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{success}</span>
                </div>
              )}

              {mode === 'email' ? (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="login-email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`${inputBase} py-2.5 pl-10`}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        name="current-password"
                        autoComplete="current-password"
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`${inputBase} py-2.5 pl-10 pr-11`}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        disabled={loading}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-500"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Signing in…
                      </>
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleMobileLogin} className="space-y-4">
                  <div>
                    <label htmlFor="login-phone" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Mobile number
                    </label>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <div className="relative flex-1">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500">
                          +91
                        </span>
                        <input
                          id="login-phone"
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel"
                          placeholder="10-digit mobile"
                          value={phone}
                          onChange={(e) =>
                            setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))
                          }
                          className={`${inputBase} py-2.5 pl-12`}
                          disabled={loading}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={loading || phone.length !== 10 || otpSent}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-500"
                      >
                        {loading && !otpSent ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Smartphone className="h-4 w-4" />
                        )}
                        {otpSent ? 'OTP sent' : 'Send OTP'}
                      </button>
                    </div>
                  </div>

                  {allowDevOtpAssistUI(exposeDevCodeFromApi) && devOtpCode && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-900">
                        On-screen test OTP (not SMS)
                      </p>
                      <p className="text-3xl font-bold tabular-nums tracking-[0.35em] text-amber-950">
                        {devOtpCode}
                      </p>
                      <p className="mt-2 text-xs leading-relaxed text-amber-900">
                        <strong>+91{phone}</strong> par SMS <strong>nahi gaya</strong> — Firebase localhost par
                        fail ho raha hai. Sign in yahi code se kar sakte ho.
                      </p>
                      {!backupSmsConfigured && (
                        <p className="mt-2 text-xs text-amber-800">
                          Real SMS: Admin → OTP → MSG91 ya Fast2SMS keys save karo, backend restart karo — phir
                          Send OTP dubara try karo.
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label htmlFor="login-otp" className="mb-1.5 block text-sm font-medium text-slate-700">
                      6-digit OTP
                    </label>
                    <input
                      id="login-otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="• • • • • •"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                      }
                      maxLength={6}
                      className={`${inputBase} py-3 text-center font-mono text-xl tracking-[0.4em]`}
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || phone.length !== 10 || otp.length !== 6}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-500"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Signing in…
                      </>
                    ) : (
                      'Sign in with OTP'
                    )}
                  </button>
                </form>
              )}

              <div className="mt-6 border-t border-slate-100 pt-6 text-center">
                <Link
                  to="/"
                  className="text-sm font-medium text-slate-600 transition-colors hover:text-primary-600"
                >
                  ← Back to store
                </Link>
              </div>
            </div>

            <p className="mt-6 text-center text-xs text-slate-500 lg:hidden">
              Secure sign-in · {storeBrand.name}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
